import { Alert, Box, Grid, Typography } from '@mui/material';
import React, { ChangeEvent } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyledBox } from 'src/components/app-components/StyledBox';
import RHFAmountField from 'src/components/react-hook-form/RHFAmountField';
import RHFonChangeTextField from 'src/components/react-hook-form/RHFonChangeTextField';
import {
  setSalesAdditionalQuestions,
  setSalesFixedAmount,
  setSalesFixedCommercialFlow,
  setSalesFixedProduct,
} from 'src/features/general/enquete-answer/store/enqueteOverviewAnswerSlice';
import useLocales from 'src/hooks/useLocales';
import { useAppDispatch, useAppSelector } from 'src/redux/store';
import { useEnqueteAnswerValidator } from '../hooks/useEnqueteAnswerValidator';

export interface EnqueteAnswerSalesFormFormProps {
  methods: UseFormReturn<FieldValues>;
}

const EnqueteAnswerSalesForm: React.FC<EnqueteAnswerSalesFormFormProps> = (props) => {
  const { methods } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isJapanese } = useLocales();
  const { salesFixedAmount, salesFixedQuestions, salesAdditionalQuestions, notConvertSalesAmount } = useAppSelector(
    (state) => state.enqueteOverviewAnswer,
  );
  const { rulesEnqueteAnswerAmount, rulesEnqueteAnswerTarget } = useEnqueteAnswerValidator();

  const prefix = 'sales-';
  return (
    <>
      <Box>{t('enqueteAnswerInput.overviewSurvey.salesTitle')}</Box>
      <StyledBox>
        <Grid container direction="column" rowSpacing={2}>
          <Grid container item direction="row" alignItems="center" justifyContent="flex-start" columns={18} columnSpacing={1}>
            <Grid item xs={3}>
              <Typography variant="subtitle1">{t('enqueteAnswerInput.overviewSurvey.amount')}</Typography>
            </Grid>
            <Grid item xs={5}>
              <RHFAmountField
                control={methods.control}
                name={`${prefix}amount}`}
                rules={rulesEnqueteAnswerAmount(salesFixedAmount.isAnswerTarget)}
                handleOnChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  dispatch(setSalesFixedAmount(event.target.value));
                }}
                defaultValue={salesFixedAmount.answer}
                textFieldProps={{
                  size: 'small',
                }}
                disabled={!salesFixedAmount.isAnswerTarget}
              />
            </Grid>

            {salesFixedQuestions.product.isAnswerTarget && (
              <>
                <Grid item xs={2}>
                  <Typography variant="subtitle1">{t('enqueteAnswerInput.overviewSurvey.product')}</Typography>
                </Grid>
                <Grid item xs={8}>
                  <RHFonChangeTextField
                    name={`${prefix}product}`}
                    defaultValue={salesFixedQuestions.product.answer}
                    control={methods.control}
                    size="small"
                    variant="outlined"
                    rules={rulesEnqueteAnswerTarget(t('enqueteAnswerInput.overviewSurvey.product'), false)}
                    handleOnChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      dispatch(setSalesFixedProduct(event.target.value));
                    }}
                    fullWidth
                  />
                </Grid>
              </>
            )}
          </Grid>
          <Grid container direction="row" columns={18}>
            <Grid item xs={3}></Grid>
            <Grid item xs={5}>
              {notConvertSalesAmount && notConvertSalesAmount.length !== 0 && salesFixedAmount.isAnswerTarget && (
                <Alert severity="warning" sx={{ marginRight: 5, marginTop: 1 }}>
                  <Grid container direction="row" alignItems="center" spacing={2}>
                    <Grid item>
                      <Typography variant="subtitle1">{t('enqueteAnswerInput.overviewSurvey.notConvertedAmount')}：</Typography>
                    </Grid>
                    <Grid item>
                      {notConvertSalesAmount.map((value, index) => (
                        <>
                          <Typography>{`${t('common.currencyCode')}:${value.currencyCode}`}</Typography>
                          <Typography>{`${t('enqueteAnswerInput.overviewSurvey.amount')}:${Number(value.amount).toLocaleString()}`}</Typography>
                        </>
                      ))}
                    </Grid>
                  </Grid>
                </Alert>
              )}
            </Grid>
          </Grid>

          {salesFixedQuestions.commercialFlow.isAnswerTarget && (
            <Grid container item direction="row" alignItems="center" justifyContent="flex-start" columns={18} columnSpacing={1}>
              <Grid item xs={3}>
                <Typography variant="subtitle1">{t('enqueteAnswerInput.overviewSurvey.commercialFlow')}</Typography>
              </Grid>
              <Grid item xs={15}>
                <RHFonChangeTextField
                  name={`${prefix}commercialFlow}`}
                  defaultValue={salesFixedQuestions.commercialFlow.answer}
                  control={methods.control}
                  size="small"
                  variant="outlined"
                  rules={rulesEnqueteAnswerTarget(t('enqueteAnswerInput.overviewSurvey.commercialFlow'), false)}
                  handleOnChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    dispatch(setSalesFixedCommercialFlow(event.target.value));
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
          {salesAdditionalQuestions &&
            salesAdditionalQuestions.map((value, index) => (
              <>
                {value.isAnswerTarget && (
                  <Grid container item direction="row" alignItems="center" justifyContent="flex-start" columns={18} columnSpacing={1}>
                    <Grid item xs={3}>
                      <Typography variant="subtitle1">{isJapanese ? value.titleJpn : value.titleEng}</Typography>
                    </Grid>
                    <Grid item xs={15}>
                      <RHFonChangeTextField
                        name={`${prefix}AddQuestions${index}`}
                        defaultValue={value.answer}
                        control={methods.control}
                        size="small"
                        variant="outlined"
                        fullWidth
                        rules={rulesEnqueteAnswerTarget(
                          isJapanese ? value.titleJpn : value.titleEng,
                          value.inputType === 'textarea',
                          value.isRequired,
                        )}
                        multiline={value.inputType === 'textarea'}
                        rows={value.inputType === 'textarea' ? 3 : 1}
                        handleOnChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                          dispatch(setSalesAdditionalQuestions({ value: event.target.value, index }));
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            ))}
        </Grid>
      </StyledBox>
    </>
  );
};

export default EnqueteAnswerSalesForm;
